import type { UserDTO } from '@krafta/shared-types';
import type { AuthHeaders } from '@shared/api/httpClient';

export const AUTH_SESSION_KEY = 'krafta_auth';
export const AUTH_SESSION_EXPIRED_EVENT = 'krafta:auth-session-expired';
let rejectedAuthorization: string | null = null;
export interface StoredAuthSession {
  token: string;
  refreshToken: string | null;
  refreshTokenExpiresAt: string | null;
  user: UserDTO;
}
const record = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === 'object' && !Array.isArray(value));
const nonempty = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;
function isUser(value: unknown): value is UserDTO {
  return record(value) && nonempty(value.id) && typeof value.email === 'string' && typeof value.fullName === 'string' &&
    Array.isArray(value.roles) && value.roles.every(nonempty) && (value.position === null || typeof value.position === 'string') &&
    typeof value.isActive === 'boolean' && typeof value.attendanceRequired === 'boolean' && typeof value.hasQrCode === 'boolean' &&
    typeof value.createdAt === 'string' && typeof value.updatedAt === 'string';
}

/** Structural validation is not authorization; the server still owns roles and permissions. */
export function parseStoredAuthSession(raw: string | null): StoredAuthSession | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (!record(value) || !nonempty(value.token) || !isUser(value.user) ||
      (value.refreshToken != null && !nonempty(value.refreshToken)) ||
      (value.refreshTokenExpiresAt != null && typeof value.refreshTokenExpiresAt !== 'string')) return null;
    if (rejectedAuthorization === `Bearer ${value.token}`) return null;
    return { token: value.token, refreshToken: value.refreshToken ?? null,
      refreshTokenExpiresAt: value.refreshTokenExpiresAt ?? null, user: value.user };
  } catch { return null; }
}

/** Explicit decision credentials must never follow a queued cross-tab employee change. */
export function assertStoredAuthHeaders(headers: AuthHeaders) {
  const session = parseStoredAuthSession(window.localStorage.getItem(AUTH_SESSION_KEY));
  if (!session || headers.Authorization !== `Bearer ${session.token}`)
    throw new Error('Сотрудник изменился. Войдите снова и повторите действие.');
}


/** A private 401 ends only the captured session; uncertain decisions remain in their actor journal. */
export function expireStoredAuthHeaders(headers: AuthHeaders) {
  const authorization = headers.Authorization;
  if (!authorization) return;
  try {
    const session = parseStoredAuthSession(window.localStorage.getItem(AUTH_SESSION_KEY));
    if (!session || authorization !== `Bearer ${session.token}`) return;
    rejectedAuthorization = authorization;
    window.localStorage.removeItem(AUTH_SESSION_KEY);
  } catch {
    // Keep this one rejected credential unusable if storage cannot remove it, including on focus.
    rejectedAuthorization = authorization;
  }
  window.dispatchEvent(new Event(AUTH_SESSION_EXPIRED_EVENT));
}
